import * as Sentry from '@sentry/vue';
import axios from '../utils/axios';

const subscriptionPrefix = '/api/subscriptions';

export const getProductsPlan = async () => {
	try {
		const result = await axios.get('/api/plans');
		return result?.data?.data || [];
	} catch {
		return [];
	}
};

export const getPaymentIntent = () => {
	return axios.post(`${subscriptionPrefix}/payment-intent`);
};

export const validateCoupon = (coupon) => {
	return axios.post(`${subscriptionPrefix}/validate-coupon/${coupon}`);
};

export const subscribe = (price_id, payment_method, coupon_code, utm_params = {}) => {
	return axios.post(`${subscriptionPrefix}/subscribe`, {
		price_id: price_id,
		payment_method: payment_method,
		coupon_code: coupon_code,
		utm_params,
	});
};

export const confirmPayment = (payment_intent) => {
	return axios.post(`${subscriptionPrefix}/confirm-payment`, { payment_intent: payment_intent });
};

export const cancel = async (payload) => {
	const data = await axios.post(`${subscriptionPrefix}/cancel`, payload);
	return data?.data;
};

/**
 * Accept the cancellation flow's 30%-off retention offer (EN-880). Takes no body — the
 * coupon and the subscription it applies to are both derived server-side.
 *
 * Unlike `cancel()`, this endpoint answers with real status codes (422 ineligible, 502
 * Stripe failure), so failures reject rather than resolving to an `{ error }` body.
 */
export const acceptRetentionOffer = async () => {
	const data = await axios.post(`${subscriptionPrefix}/retention-offer/accept`);
	return data?.data;
};

/**
 * The features the user leans on most, for the cancellation flow's usage-impact step (EN-881).
 * Array order is the ranking, most-used first — render it as given rather than re-sorting.
 *
 * Failures reject rather than resolving to an empty list: the step that consumes this has to
 * tell "nothing to show" apart from "the request never landed", and only reacts to the latter.
 */
export const getUsageSummary = async () => {
	const data = await axios.get(`${subscriptionPrefix}/usage-summary`);
	return data?.data?.data?.features ?? [];
};

/**
 * Fire-and-forget tracking for the reason picked on the cancellation flow's reason step.
 * Emits the Klaviyo event; failures are swallowed so tracking never blocks the flow.
 *
 * @param {string} reason - A SubscriptionCancellationReason value
 */
export const trackCancellationReasonSelected = (reason) => {
	return axios.post(`${subscriptionPrefix}/cancellation/reason-selected`, { reason }).catch(() => {});
};

export const resumeSubscription = async (price_id) => {
	const data = await axios.post(`${subscriptionPrefix}/resume`, { price_id: price_id });
	return data?.data;
};

export const getGracePeriodStatus = async () => {
	try {
		const result = await axios.get(`${subscriptionPrefix}/grace-period-status`);
		return result?.data;
	} catch (err) {
		if (err?.response?.status !== 429) {
			Sentry.captureMessage(`Failed to fetch grace period status: ${err.message}`, 'error');
		}
		return null;
	}
};

export const clearGracePeriod = async () => {
	const result = await axios.post(`${subscriptionPrefix}/clear-grace-period-failed-payments`);
	return result?.data;
};
